import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import Notification from '../../components/Notification/Notification'
import { ALL_FODBOLDHOLD, MY_FODBOLDHOLD } from '../../graphql/queries'
import { diffActivity } from './activity'
import {
  ADD_FODBOLDHOLD_COMMENT,
  ADD_FODBOLDHOLD_EVENT,
  ADD_FODBOLDHOLD_EVENT_MESSAGE,
  ADD_FODBOLDHOLD_MATCH,
  UPDATE_FODBOLDHOLD_MATCH,
  ADD_FODBOLDHOLD_MATCH_MESSAGE,
  ADD_FODBOLDHOLD_MATCH_COMMENT,
  ADD_FODBOLDHOLD_POST,
  ADD_FODBOLDHOLD_SEASON,
  CREATE_FODBOLDHOLD,
  DELETE_FODBOLDHOLD_COMMENT,
  DELETE_FODBOLDHOLD_MATCH_MESSAGE,
  DELETE_FODBOLDHOLD_POST,
  TOGGLE_FODBOLDHOLD_POST_LIKE,
  TOGGLE_FODBOLDHOLD_MATCH_MESSAGE_LIKE,
  IMPORT_FODBOLDHOLD_MATCHES,
  JOIN_FODBOLDHOLD,
  SET_FODBOLDHOLD_ADMIN,
  SET_FODBOLDHOLD_FINE_CHAIR,
  SET_MY_FODBOLDHOLD_FINE_BOX,
  CREATE_FODBOLDHOLD_FINE_TYPE,
  UPDATE_FODBOLDHOLD_FINE_TYPE,
  DELETE_FODBOLDHOLD_FINE_TYPE,
  GIVE_FODBOLDHOLD_FINES,
  UPDATE_FODBOLDHOLD_FINE_GROUP,
  DELETE_FODBOLDHOLD_FINE,
  SET_FODBOLDHOLD_FINE_PAID,
  SET_FODBOLDHOLD_FINES_PAID,
  SET_FODBOLDHOLD_FINE_BOX,
  CLAIM_FODBOLDHOLD_FINE_PAYMENT,
  DECIDE_FODBOLDHOLD_FINE_CLAIM,
  SET_FODBOLDHOLD_FINE_PAYMENT,
  SET_FODBOLDHOLD_FEE,
  SET_FODBOLDHOLD_STANDINGS_URL,
  SET_FODBOLDHOLD_GOAL,
  TOGGLE_FODBOLDHOLD_EVENT,
  TOGGLE_FODBOLDHOLD_MATCH,
  SET_FODBOLDHOLD_MATCH_REPLY,
  SET_FODBOLDHOLD_EVENT_REPLY,
  TOGGLE_FODBOLDHOLD_PAID,
  UPDATE_FODBOLDHOLD,
  UPDATE_FODBOLDHOLD_COMMENT,
  UPDATE_FODBOLDHOLD_MATCH_MESSAGE,
  UPDATE_FODBOLDHOLD_POST,
} from '../../graphql/mutations'
const TeamContext = createContext(null)

function mapTeam(team) {
  return {
    id: team.id,
    name: team.name,
    initials: team.initials,
    logo: team.logo || null,
    background: team.background || null,
    mobilePay: team.mobilePay || '',
    fineBoxId: team.fineBoxId || '',
    icalUrl: team.icalUrl || '',
    inviteCode: team.inviteCode || '',
    members: (team.members || []).map((member) => ({
      id: member.mateId,
      name: member.name,
      isAdmin: member.isAdmin,
      fineChair: Boolean(member.fineChair),
      inFineBox: Boolean(member.inFineBox),
    })),
    fineTypes: (team.fineTypes || []).map((type) => ({
      id: type.id,
      text: type.text,
      amount: type.amount || 0,
    })),
    news: team.news || [],
    posts: (team.posts || []).map((post) => ({
      ...post,
      image: post.image || null,
      likedBy: post.likedBy || [],
      comments: post.comments || [],
    })),
    seasons: (team.seasons || []).map((season) => ({
      id: season.id,
      name: season.name,
      current: season.current,
      fee: {
        amount: season.feeAmount || 0,
        due: season.feeDue || '',
        paid: Object.fromEntries((season.paidMateIds || []).map((id) => [id, true])),
      },
      standingsUrl: season.standingsUrl || '',
      matches: (season.matches || []).map((match) => ({
        ...match,
        meeting: match.meeting || '',
        note: match.note || '',
        max: match.max ?? null,
        attendees: match.attendees || [],
        waitlist: match.waitlist || [],
        declined: match.declined || [],
        goals: match.goals || [],
        messages: (match.messages || []).map((message) => ({
          ...message,
          image: message.image || null,
          likedBy: message.likedBy || [],
          comments: message.comments || [],
        })),
      })),
      events: (season.events || []).map((event) => ({
        ...event,
        description: event.description || '',
        max: event.max ?? null,
        attendees: event.attendees || [],
        waitlist: event.waitlist || [],
        declined: event.declined || [],
        messages: (event.messages || []).map((message) => ({
          ...message,
          image: message.image || null,
          likedBy: message.likedBy || [],
          comments: message.comments || [],
        })),
      })),
      fines: (season.fines || []).map((fine) => ({
        ...fine,
        matchId: fine.matchId || null,
        paid: Boolean(fine.paid),
        amount: fine.amount || 0,
      })),
      finePayments: (season.finePayments || []).map((payment) => ({
        mateId: payment.mateId,
        amount: payment.amount || 0,
      })),
      fineClaims: (season.fineClaims || []).map((claim) => ({
        id: claim.id,
        mateId: claim.mateId,
        amount: claim.amount || 0,
        createdAt: claim.createdAt,
      })),
      fineApprovals: (season.fineApprovals || []).map((approval) => ({
        id: approval.id,
        mateId: approval.mateId,
        amount: approval.amount || 0,
        approvedBy: approval.approvedBy,
        approvedByName: approval.approvedByName || 'Ukendt',
        kind: approval.kind === 'registered' ? 'registered' : 'approved',
        createdAt: approval.createdAt,
      })),
    })),
  }
}

function mapTeams(list) {
  return (list || []).map(mapTeam)
}

function matchInput(match) {
  return {
    opponent: match.opponent,
    home: Boolean(match.home),
    kickoff: match.kickoff,
    place: match.place,
    meeting: match.meeting || '',
    note: match.note || '',
    max: match.max ?? null,
  }
}

function teamInput(fields) {
  const input = {}
  if (fields.mobilePay !== undefined) input.mobilePay = fields.mobilePay
  if (fields.icalUrl !== undefined) input.icalUrl = fields.icalUrl
  if (fields.logo !== undefined) input.logo = fields.logo
  if (fields.background !== undefined) input.background = fields.background
  return input
}

async function runMutation(client, action) {
  switch (action.type) {
    case 'ADD_TEAM':
      return client.mutate({
        mutation: CREATE_FODBOLDHOLD,
        variables: { name: action.name.trim(), seasonName: action.seasonName.trim() },
      })
    case 'JOIN':
      return client.mutate({
        mutation: JOIN_FODBOLDHOLD,
        variables: { inviteCode: action.inviteCode },
      })
    case 'SET_TEAM':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD,
        variables: { teamId: action.teamId, input: teamInput(action.fields) },
      })
    case 'SET_HOLD_ADMIN':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_ADMIN,
        variables: { teamId: action.teamId, mateId: action.mateId, isAdmin: action.isAdmin },
      })
    case 'SET_FINE_CHAIR':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FINE_CHAIR,
        variables: { teamId: action.teamId, mateId: action.mateId, fineChair: action.fineChair },
      })
    case 'SET_MY_FINE_BOX':
      return client.mutate({
        mutation: SET_MY_FODBOLDHOLD_FINE_BOX,
        variables: { teamId: action.teamId, inFineBox: action.inFineBox },
      })
    case 'CREATE_FINE_TYPE':
      return client.mutate({
        mutation: CREATE_FODBOLDHOLD_FINE_TYPE,
        variables: { teamId: action.teamId, text: action.text, amount: action.amount },
      })
    case 'UPDATE_FINE_TYPE':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_FINE_TYPE,
        variables: { teamId: action.teamId, typeId: action.typeId, text: action.text, amount: action.amount },
      })
    case 'DELETE_FINE_TYPE':
      return client.mutate({
        mutation: DELETE_FODBOLDHOLD_FINE_TYPE,
        variables: { teamId: action.teamId, typeId: action.typeId },
      })
    case 'GIVE_FINES':
      return client.mutate({
        mutation: GIVE_FODBOLDHOLD_FINES,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          typeId: action.typeId || null,
          text: action.text || null,
          amount: action.amount ?? null,
          mateIds: action.mateIds,
          matchId: action.matchId || null,
          count: action.count || 1,
        },
      })
    case 'UPDATE_FINE_GROUP':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_FINE_GROUP,
        variables: {
          teamId: action.teamId,
          fineIds: action.fineIds,
          text: action.text,
          amount: action.amount,
          count: action.count,
        },
      })
    case 'DELETE_FINE':
      return client.mutate({
        mutation: DELETE_FODBOLDHOLD_FINE,
        variables: { teamId: action.teamId, fineId: action.fineId },
      })
    case 'SET_FINE_PAID':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FINE_PAID,
        variables: { teamId: action.teamId, fineId: action.fineId, paid: action.paid },
      })
    case 'SET_FINES_PAID':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FINES_PAID,
        variables: { teamId: action.teamId, seasonId: action.seasonId, mateId: action.mateId },
      })
    case 'SET_FINE_BOX':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FINE_BOX,
        variables: { teamId: action.teamId, link: action.link },
      })
    case 'CLAIM_FINE_PAYMENT':
      return client.mutate({
        mutation: CLAIM_FODBOLDHOLD_FINE_PAYMENT,
        variables: { teamId: action.teamId, seasonId: action.seasonId },
      })
    case 'DECIDE_FINE_CLAIM':
      return client.mutate({
        mutation: DECIDE_FODBOLDHOLD_FINE_CLAIM,
        variables: { teamId: action.teamId, claimId: action.claimId, approve: action.approve },
      })
    case 'SET_FINE_PAYMENT':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FINE_PAYMENT,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          mateId: action.mateId,
          amount: action.amount,
          entry: action.entry ?? null,
        },
      })
    case 'SET_FEE':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_FEE,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          amount: action.amount,
          due: action.due || '',
        },
      })
    case 'SET_STANDINGS':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_STANDINGS_URL,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          url: action.url || '',
        },
      })
    case 'TOGGLE_PAID':
      return client.mutate({
        mutation: TOGGLE_FODBOLDHOLD_PAID,
        variables: { teamId: action.teamId, seasonId: action.seasonId, mateId: action.playerId },
      })
    case 'IMPORT_MATCHES':
      return client.mutate({
        mutation: IMPORT_FODBOLDHOLD_MATCHES,
        variables: { teamId: action.teamId, seasonId: action.seasonId },
      })
    case 'ADD_MATCH':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_MATCH,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          input: matchInput(action.match),
        },
      })
    case 'UPDATE_MATCH':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_MATCH,
        variables: {
          teamId: action.teamId,
          matchId: action.matchId,
          input: matchInput(action.match),
        },
      })
    case 'TOGGLE_MATCH':
      return client.mutate({
        mutation: TOGGLE_FODBOLDHOLD_MATCH,
        variables: { teamId: action.teamId, matchId: action.matchId },
      })
    case 'SET_MATCH_REPLY':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_MATCH_REPLY,
        variables: { teamId: action.teamId, matchId: action.matchId, reply: action.reply },
      })
    case 'SET_GOAL':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_GOAL,
        variables: {
          teamId: action.teamId,
          matchId: action.matchId,
          playerId: action.playerId,
          count: action.count,
        },
      })
    case 'ADD_MATCH_MESSAGE':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_MATCH_MESSAGE,
        variables: {
          teamId: action.teamId,
          matchId: action.matchId,
          text: action.text,
          image: action.image,
        },
      })
    case 'UPDATE_MATCH_MESSAGE':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_MATCH_MESSAGE,
        variables: {
          teamId: action.teamId,
          messageId: action.messageId,
          text: action.text,
          image: action.image,
        },
      })
    case 'DELETE_MATCH_MESSAGE':
      return client.mutate({
        mutation: DELETE_FODBOLDHOLD_MATCH_MESSAGE,
        variables: { teamId: action.teamId, messageId: action.messageId },
      })
    case 'TOGGLE_MATCH_LIKE':
      return client.mutate({
        mutation: TOGGLE_FODBOLDHOLD_MATCH_MESSAGE_LIKE,
        variables: { teamId: action.teamId, messageId: action.messageId },
      })
    case 'ADD_MATCH_COMMENT':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_MATCH_COMMENT,
        variables: { teamId: action.teamId, messageId: action.messageId, text: action.text },
      })
    case 'ADD_EVENT_MESSAGE':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_EVENT_MESSAGE,
        variables: {
          teamId: action.teamId,
          eventId: action.eventId,
          text: action.text,
          image: action.image,
        },
      })
    case 'ADD_EVENT':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_EVENT,
        variables: {
          teamId: action.teamId,
          seasonId: action.seasonId,
          input: {
            title: action.event.title,
            kickoff: action.event.kickoff,
            place: action.event.place,
            description: action.event.description || '',
            max: action.event.max,
          },
        },
      })
    case 'TOGGLE_EVENT':
      return client.mutate({
        mutation: TOGGLE_FODBOLDHOLD_EVENT,
        variables: { teamId: action.teamId, eventId: action.eventId },
      })
    case 'SET_EVENT_REPLY':
      return client.mutate({
        mutation: SET_FODBOLDHOLD_EVENT_REPLY,
        variables: { teamId: action.teamId, eventId: action.eventId, reply: action.reply },
      })
    case 'ADD_POST':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_POST,
        variables: { teamId: action.teamId, text: action.text, image: action.image },
      })
    case 'UPDATE_POST':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_POST,
        variables: { teamId: action.teamId, postId: action.postId, text: action.text, image: action.image },
      })
    case 'TOGGLE_POST_LIKE':
      return client.mutate({
        mutation: TOGGLE_FODBOLDHOLD_POST_LIKE,
        variables: { teamId: action.teamId, postId: action.postId },
      })
    case 'DELETE_POST':
      return client.mutate({
        mutation: DELETE_FODBOLDHOLD_POST,
        variables: { teamId: action.teamId, postId: action.postId },
      })
    case 'ADD_COMMENT':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_COMMENT,
        variables: { teamId: action.teamId, postId: action.postId, text: action.text },
      })
    case 'UPDATE_COMMENT':
      return client.mutate({
        mutation: UPDATE_FODBOLDHOLD_COMMENT,
        variables: { teamId: action.teamId, commentId: action.commentId, text: action.text },
      })
    case 'DELETE_COMMENT':
      return client.mutate({
        mutation: DELETE_FODBOLDHOLD_COMMENT,
        variables: { teamId: action.teamId, commentId: action.commentId },
      })
    case 'ADD_SEASON':
      return client.mutate({
        mutation: ADD_FODBOLDHOLD_SEASON,
        variables: { teamId: action.teamId, name: action.name.trim() },
      })
    default:
      return null
  }
}

function errorMessage(error) {
  if (!error) return ''
  return error.graphQLErrors?.[0]?.message || error.message || 'Noget gik galt'
}

export function findTeam(state, teamId) {
  return state.teams.find((team) => team.id === teamId)
    || state.allTeams.find((team) => team.id === teamId)
    || null
}

export function TeamProvider({ children }) {
  const client = useApolloClient()
  const location = useLocation()
  const onHold = location.pathname.startsWith('/hold')
  const meId = useSelector((redux) => redux.mate.currentUser?.id) || null
  const isSvirrensAdmin = useSelector((redux) => redux.mate.currentUser?.role === 'admin')
  const mine = useQuery(MY_FODBOLDHOLD, {
    skip: !meId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const all = useQuery(ALL_FODBOLDHOLD, {
    skip: !isSvirrensAdmin,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const [viewing, setViewing] = useState({})
  const [notices, setNotices] = useState([])
  const [actionError, setActionError] = useState('')
  const [activityNotice, setActivityNotice] = useState(null)
  const activityQueue = useRef([])
  const seenTeams = useRef(null)
  const skipActivity = useRef(false)

  const refetchMine = useRef(mine.refetch)
  const refetchAll = useRef(all.refetch)
  const meIdRef = useRef(meId)
  const isAdminRef = useRef(isSvirrensAdmin)
  const wasOnHold = useRef(false)
  refetchMine.current = mine.refetch
  refetchAll.current = all.refetch
  meIdRef.current = meId
  isAdminRef.current = isSvirrensAdmin

  const pushActivity = useCallback((items) => {
    const batch = items.slice(0, 3)
    activityQueue.current.push(...batch)
    setActivityNotice((current) => current || activityQueue.current.shift() || null)
  }, [])

  const closeActivity = useCallback(() => {
    setActivityNotice(activityQueue.current.shift() || null)
  }, [])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && meIdRef.current) refetchMine.current()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    const entered = onHold && !wasOnHold.current
    wasOnHold.current = onHold
    if (!entered || !meIdRef.current || !seenTeams.current) return
    refetchMine.current()
  }, [onHold])

  const teams = useMemo(() => mapTeams(mine.data?.myFodboldhold), [mine.data])
  const allTeams = useMemo(
    () => mapTeams(isSvirrensAdmin ? all.data?.allFodboldhold : mine.data?.myFodboldhold),
    [all.data, isSvirrensAdmin, mine.data],
  )
  const queryError = mine.error

  const dispatch = useCallback(async (action) => {
    if (action.type === 'SET_VIEWING') {
      setViewing((current) => ({ ...current, [action.teamId]: action.seasonId }))
      return { ok: true }
    }
    if (action.type === 'DISMISS_NOTICE') {
      setNotices((current) => current.filter((notice) => notice.id !== action.id))
      return { ok: true }
    }
    setActionError('')
    skipActivity.current = true
    try {
      const result = await runMutation(client, action)
      const payload = result?.data ? Object.values(result.data)[0] : null
      if (action.type === 'ADD_SEASON') {
        const season = payload?.seasons?.find((item) => item.current)
        if (season) setViewing((current) => ({ ...current, [action.teamId]: season.id }))
      }
      await mine.refetch()
      if (isSvirrensAdmin) await all.refetch()
      return { ok: true, teamId: payload?.id || null }
    } catch (error) {
      skipActivity.current = false
      const message = errorMessage(error)
      setActionError(message)
      return { ok: false, error: message }
    }
  }, [all, client, isSvirrensAdmin, mine])

  useEffect(() => {
    if (!meId) {
      seenTeams.current = null
      return
    }
    if (!mine.data) return
    if (!seenTeams.current) {
      seenTeams.current = teams
      return
    }
    const notes = diffActivity(seenTeams.current, teams, meId).map((note) => ({
      ...note,
      logo: teams.find((team) => team.id === note.teamId)?.logo || null,
    }))
    seenTeams.current = teams
    if (notes.length === 0) return
    if (skipActivity.current) {
      skipActivity.current = false
      return
    }
    pushActivity(notes)
  }, [meId, mine.data, pushActivity, teams])

  const refresh = useCallback(async () => {
    if (!meIdRef.current) return
    const jobs = [refetchMine.current()]
    if (isAdminRef.current) jobs.push(refetchAll.current())
    await Promise.all(jobs)
  }, [])

  const value = useMemo(() => ({
    meId,
    dispatch,
    refresh,
    state: {
      teams,
      allTeams,
      viewing,
      notices,
      meId,
      loading: Boolean(meId) && mine.loading && !mine.data,
      loadingAll: Boolean(isSvirrensAdmin) && all.loading && !all.data,
      error: actionError || errorMessage(queryError),
      adminError: errorMessage(all.error),
    },
  }), [
    actionError,
    all.loading,
    all.data,
    allTeams,
    dispatch,
    refresh,
    isSvirrensAdmin,
    meId,
    mine.data,
    mine.loading,
    notices,
    all.error,
    queryError,
    teams,
    viewing,
  ])

  return (
    <TeamContext.Provider value={value}>
      {children}
      <Notification
        key={activityNotice?.id || 'hold-activity'}
        open={Boolean(activityNotice)}
        message={activityNotice?.message || ''}
        type="info"
        icon={activityNotice?.logo ? <img src={activityNotice.logo} alt="" /> : undefined}
        onClose={closeActivity}
      />
    </TeamContext.Provider>
  )
}

export function useTeamState() {
  return useContext(TeamContext)
}

export function useHold() {
  const { teamId } = useParams()
  const context = useContext(TeamContext)
  const youName = useSelector((redux) => redux.mate.currentUser?.name) || ''
  const team = findTeam(context.state, teamId)
  const current = team?.seasons.find((season) => season.current) || null
  const viewingId = context.state.viewing[teamId] || current?.id
  const season = team?.seasons.find((item) => item.id === viewingId) || current
  const me = team?.members.find((member) => member.id === context.meId) || null

  const nameOf = (playerId) => {
    if (playerId === context.meId && youName) return youName
    return team?.members.find((member) => member.id === playerId)?.name || 'Ukendt'
  }

  return {
    team,
    season,
    current,
    me,
    meId: context.meId,
    isAdmin: Boolean(me?.isAdmin),
    youName,
    loading: context.state.loading,
    dispatch: context.dispatch,
    nameOf,
  }
}
